import type { ComponentType, ReactNode } from 'react';

import { EmptyState } from '@/components/ui';

/**
 * KanbanListView — vista tabela (R5.1 Bloco D / FEAT-V2-006).
 *
 * Componente genérico que delega cada linha ao `rowComponent`. Caller define
 * colunas via header customizado (passado como children/header slot) e cada
 * linha decide como renderizar.
 *
 * Filosofia: simples e dumb — não tenta abstrair sorting/filtering/pagination.
 * Esses comportamentos vivem no caller. List view é só "vista plana
 * cronológica" do Kanban, agnóstica de schema.
 */

export interface KanbanListRowProps<T> {
  item: T;
  /** Click na row — caller decide o que fazer (ex: abrir detail). */
  onClick: () => void;
}

export interface KanbanListViewProps<T extends { id: string }> {
  items: T[];
  /** Componente que renderiza UMA linha. Geralmente um <tr> ou <li>. */
  rowComponent: ComponentType<KanbanListRowProps<T>>;
  /** Click numa row — handler unificado. */
  onItemClick?: (item: T) => void;
  /** Slot pra header da tabela (opcional). */
  header?: ReactNode;
  /** Empty state quando items=[]. Default copy genérico. */
  emptyTitle?: string;
  emptyDescription?: string;
}

export function KanbanListView<T extends { id: string }>(props: KanbanListViewProps<T>) {
  const {
    items,
    rowComponent: RowComponent,
    onItemClick,
    header,
    emptyTitle = 'Nenhum item',
    emptyDescription,
  } = props;

  if (items.length === 0) {
    return (
      <div data-testid="kanban-list-empty" className="py-8">
        <EmptyState title={emptyTitle} description={emptyDescription} />
      </div>
    );
  }

  return (
    <div data-testid="kanban-list-view" className="space-y-2">
      {header && (
        <div className="px-3 py-2 text-xs uppercase tracking-wider text-ink-3 dark:text-ink-dark-3 border-b border-line dark:border-line-dark">
          {header}
        </div>
      )}
      <ul className="space-y-1.5">
        {items.map((item) => (
          <RowComponent
            key={item.id}
            item={item}
            onClick={() => onItemClick?.(item)}
          />
        ))}
      </ul>
    </div>
  );
}
